import React, { useState, useRef, useEffect, useCallback } from 'react';

interface StyledSelectOption {
  value: string;
  label: string;
}

interface StyledSelectProps {
  options: StyledSelectOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
  testId?: string;
}

export function StyledSelect({ options, value, onChange, ariaLabel, className = '', testId }: StyledSelectProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(o => o.value === value) ?? options[0];

  const close = useCallback(() => {
    setOpen(false);
    setFocusIndex(-1);
  }, []);

  const open_ = useCallback(() => {
    const idx = options.findIndex(o => o.value === value);
    setOpen(true);
    setFocusIndex(idx >= 0 ? idx : 0);
  }, [options, value]);

  const select = useCallback((val: string) => {
    onChange(val);
    close();
    triggerRef.current?.focus();
  }, [onChange, close]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  // Scroll focused option into view
  useEffect(() => {
    if (!open || focusIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    items[focusIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, focusIndex]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open_();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusIndex(prev => Math.min(prev + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusIndex >= 0 && focusIndex < options.length) {
          select(options[focusIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        triggerRef.current?.focus();
        break;
      case 'Tab':
        close();
        break;
    }
  }, [open, open_, close, select, focusIndex, options]);

  return (
    <div
      ref={containerRef}
      className={`styled-select relative ${className}`.trim()}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className="styled-select__trigger w-full h-9 text-sm font-medium rounded-md px-2 py-1 border flex items-center justify-between gap-1 transition-colors cursor-pointer"
        style={{
          background: 'var(--color-bg-alt)',
          borderColor: 'var(--color-border)',
          color: 'var(--color-text)',
        }}
        onClick={() => (open ? close() : open_())}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className="truncate">{selectedOption?.label}</span>
        <svg
          aria-hidden
          className={`shrink-0 w-4 h-4 transition-transform duration-150 ${open ? '' : 'rotate-180'}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          className="styled-select__dropdown absolute z-50 left-0 right-0 bottom-full mb-1 py-1 rounded-md border shadow-lg max-h-48 overflow-y-auto overscroll-contain scrollbar-thin"
          style={{
            background: 'var(--color-bg-alt)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text)',
          }}
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {options.map((option, idx) => {
            const isSelected = option.value === value;
            const isFocused = idx === focusIndex;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                className="styled-select__option flex items-center gap-2 px-2 py-1.5 text-sm font-medium cursor-pointer transition-colors"
                style={{
                  background: isFocused
                    ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)'
                    : isSelected
                      ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)'
                      : undefined,
                  color: isSelected ? 'var(--color-accent)' : undefined,
                }}
                onMouseEnter={() => setFocusIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option.value);
                }}
                data-testid={testId ? `${testId}-option-${option.value}` : undefined}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && (
                  <svg aria-hidden className="shrink-0 w-4 h-4 ml-auto" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
